import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Print as PrintIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { get, authPost, authPut, authDelete } from '../../services/apiService';
import { isAdmin, isAuthenticated } from '../../services/authService';
import { format } from 'date-fns';

const darkGreenColor = "#1D4434";

/**
 * HIPAA/CUI Documentation - Shows documentation tracking for HIPAA/CUI compliance
 * @returns {JSX.Element} HipaaCuiDocumentation component
 */
const HipaaCuiDocumentation = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentation, setDocumentation] = useState([]);
  const [filteredDocumentation, setFilteredDocumentation] = useState([]);
  
  // Modal state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({
    documentTitle: '',
    externalLink: '',
    comments: ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      const response = await get('/api/hipaa-cui/documentation');
      
      if (response.success) {
        setDocumentation(response.data || []);
      }
    } catch (err) {
      console.error('Error loading HIPAA/CUI documentation:', err);
      setError(err.message || 'Failed to load documentation data');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const applyFilters = useCallback(() => {
    // No filters for now, but keeping the structure for future use
    setFilteredDocumentation([...documentation]);
  }, [documentation]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleOpenDialog = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setFormData({
        documentTitle: doc.documentTitle || '',
        externalLink: doc.externalLink || '',
        comments: doc.comments || ''
      });
    } else {
      setEditingDoc(null);
      setFormData({
        documentTitle: '',
        externalLink: '',
        comments: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDoc(null);
    setFormData({
      documentTitle: '',
      externalLink: '',
      comments: ''
    });
  };

  const handleSaveDocumentation = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.documentTitle.trim()) {
        setError('Document Title is required');
        setSaving(false);
        return;
      }

      let response;
      if (editingDoc) {
        response = await authPut(`/api/hipaa-cui/documentation/${editingDoc._id}`, formData);
      } else {
        response = await authPost('/api/hipaa-cui/documentation', formData);
      }

      if (response.success) {
        handleCloseDialog();
        await loadData(false);
      } else {
        setError(response.message || 'Failed to save documentation');
      }
    } catch (err) {
      console.error('Error saving documentation:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save documentation');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocumentation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this documentation entry?')) {
      return;
    }

    try {
      setDeleting(id);
      setError(null);

      const response = await authDelete(`/api/hipaa-cui/documentation/${id}`);

      if (response.success) {
        await loadData(false);
      } else {
        setError(response.message || 'Failed to delete documentation');
      }
    } catch (err) {
      console.error('Error deleting documentation:', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete documentation');
    } finally {
      setDeleting(null);
    }
  };

  const handleDownloadJson = () => {
    try {
      const cleanedDocs = documentation.map(({ _id, __v, createdAt, updatedAt, createdBy, updatedBy, ...rest }) => rest);
      const dataStr = JSON.stringify(cleanedDocs, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hipaa-cui-documentation-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading JSON:', err);
      setError('Failed to download JSON file');
    }
  };

  const handleUploadJson = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      if (!Array.isArray(jsonData)) {
        setError('JSON file must contain an array of documentation objects');
        return;
      }

      const response = await authPost('/api/hipaa-cui/documentation/bulk-import', {
        documentation: jsonData,
        clearExisting: false
      });

      if (response.success) {
        const summary = response.summary || {};
        const message = [
          response.message,
          summary.errors && summary.errors.length > 0 && `${summary.errors.length} error(s) occurred`
        ].filter(Boolean).join('. ');
        
        setSuccessMessage(message);
        if (summary.errors && summary.errors.length > 0) {
          setError(`Errors: ${summary.errors.slice(0, 5).join('; ')}${summary.errors.length > 5 ? ' ...' : ''}`);
        }
        await loadData(false);
        setTimeout(() => {
          setSuccessMessage(null);
          setError(null);
        }, 10000);
      } else {
        setError(response.message || 'Failed to import documentation');
      }
    } catch (err) {
      console.error('Error uploading JSON:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload JSON file. Please check the file format.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handlePrint = () => {
    try {
      const printWindow = window.open('', '_blank');
      const printDate = new Date().toLocaleString();
      
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>HIPAA/CUI Documentation List</title>
          <style>
            @media print {
              @page {
                margin: 1cm;
                size: A4 landscape;
              }
              body {
                margin: 0;
                padding: 0;
              }
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
              padding: 20px;
            }
            h1 {
              color: #1D4434;
              margin-bottom: 10px;
              border-bottom: 2px solid #1D4434;
              padding-bottom: 10px;
            }
            .print-info {
              margin-bottom: 20px;
              color: #666;
              font-size: 11px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th {
              background-color: #1D4434;
              color: white;
              padding: 10px;
              text-align: left;
              border: 1px solid #ddd;
              font-weight: bold;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            a {
              color: #1976d2;
              text-decoration: underline;
            }
          </style>
        </head>
        <body>
          <h1>HIPAA/CUI Documentation List</h1>
          <div class="print-info">
            Printed on: ${printDate} | Total Records: ${filteredDocumentation.length}
          </div>
          <table>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Document Title</th>
                <th>External Link</th>
                <th>Comments</th>
                <th>Last Changed By</th>
                <th>Changed Date</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      filteredDocumentation.forEach((doc, index) => {
        htmlContent += `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(doc.documentTitle || '-')}</td>
            <td>${doc.externalLink ? `<a href="${escapeHtml(doc.externalLink)}" target="_blank">${escapeHtml(doc.externalLink)}</a>` : '-'}</td>
            <td>${escapeHtml(doc.comments || '-')}</td>
            <td>${escapeHtml(doc.updatedBy?.username || '-')}</td>
            <td>${doc.updatedAt ? format(new Date(doc.updatedAt), 'MMM dd, yyyy HH:mm') : '-'}</td>
          </tr>
        `;
      });
      
      htmlContent += `
            </tbody>
          </table>
        </body>
        </html>
      `;
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    } catch (err) {
      console.error('Error printing:', err);
      setError('Failed to print table');
    }
  };

  const escapeHtml = (text) => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Helper function to truncate URL for display
  const truncateUrl = (url, maxLength = 40) => {
    if (!url) return '';
    if (url.length <= maxLength) return url;
    
    // Try to show the domain and a bit of the path
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;
      const path = urlObj.pathname;
      
      // If domain + path is short enough, show it
      const fullPath = domain + path;
      if (fullPath.length <= maxLength) {
        return fullPath;
      }
      
      // Otherwise, show domain + truncated path
      const availableForPath = maxLength - domain.length - 3; // 3 for "..."
      if (availableForPath > 0) {
        return domain + path.substring(0, availableForPath) + '...';
      }
      
      // If domain itself is too long, truncate it
      return domain.substring(0, maxLength - 3) + '...';
    } catch (e) {
      // If URL parsing fails, just truncate the string
      return url.substring(0, maxLength - 3) + '...';
    }
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Actions Bar - No filters for Documentation */}
      {isAuthenticated() && (
        <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 1 }}>
          <Box display="flex" gap={0.75} flexWrap="wrap" justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                backgroundColor: darkGreenColor,
                '&:hover': { backgroundColor: '#0f2e1f' }
              }}
            >
              Add
            </Button>
            {isAdmin() && (
              <Button
                variant="outlined"
                size="small"
                startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
                component="label"
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
                <input
                  type="file"
                  accept=".json,application/json"
                  hidden
                  onChange={handleUploadJson}
                  disabled={uploading}
                />
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadJson}
              disabled={documentation.length === 0}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={filteredDocumentation.length === 0}
            >
              Print
            </Button>
          </Box>
        </Paper>
      )}

      {/* Documentation Table - Full Height */}
      {loading ? (
        <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, flex: 1 }}>
          <Box textAlign="center">
            <CircularProgress size={50} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading documentation...
            </Typography>
          </Box>
        </Paper>
      ) : (
        <TableContainer 
          component={Paper} 
          elevation={0}
          sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            '& .MuiTable-root': {
              minWidth: 650
            }
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2,
                    '&:first-of-type': {
                      borderTopLeftRadius: '8px'
                    }
                  }}
                  width="80"
                >
                  Sr. No
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                  width="30%"
                >
                  Document Title
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                  width="20%"
                >
                  External Link
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                >
                  Comments
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                >
                  Last Changed By
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                >
                  Changed Date
                </TableCell>
                {isAuthenticated() && (
                  <TableCell 
                    align="right"
                    width="120"
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2,
                      '&:last-of-type': {
                        borderTopRightRadius: '8px'
                      }
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDocumentation.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAuthenticated() ? 7 : 6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                      No documentation found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocumentation.map((doc, index) => (
                  <TableRow 
                    key={doc._id}
                    sx={{
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(29, 68, 52, 0.04)',
                        transform: 'scale(1.001)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      },
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary', py: 2 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: darkGreenColor, wordBreak: 'break-word' }}>
                        {doc.documentTitle}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {doc.externalLink ? (
                        <Tooltip title={doc.externalLink} arrow>
                          <Box
                            component="a"
                            href={doc.externalLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'primary.main',
                              textDecoration: 'none',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.dark'
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 'calc(100% - 20px)'
                              }}
                            >
                              {truncateUrl(doc.externalLink, 35)}
                            </Typography>
                            <OpenInNewIcon fontSize="small" sx={{ fontSize: '0.875rem', flexShrink: 0 }} />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2, maxWidth: 300 }}>
                      <Tooltip title={doc.comments || '-'} arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.secondary'
                          }}
                        >
                          {doc.comments || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {doc.updatedBy?.username || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {doc.updatedAt ? format(new Date(doc.updatedAt), 'MMM dd, yyyy HH:mm') : '-'}
                      </Typography>
                    </TableCell>
                    {isAuthenticated() && (
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(doc)}
                              sx={{
                                color: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'primary.dark',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteDocumentation(doc._id)}
                              disabled={deleting === doc._id}
                              sx={{
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  color: 'error.dark',
                                  transform: 'scale(1.1)'
                                },
                                '&:disabled': {
                                  opacity: 0.5
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {deleting === doc._id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDoc ? 'Edit Documentation' : 'Add Documentation'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Document Title"
                  value={formData.documentTitle}
                  onChange={(e) => setFormData({ ...formData, documentTitle: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="External Link"
                  value={formData.externalLink}
                  onChange={(e) => setFormData({ ...formData, externalLink: e.target.value })}
                  placeholder="https://..."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comments"
                  value={formData.comments}
                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveDocumentation}
            variant="contained"
            disabled={saving || !formData.documentTitle.trim()}
            sx={{
              backgroundColor: darkGreenColor,
              '&:hover': { backgroundColor: '#0f2e1f' }
            }}
          >
            {saving ? 'Saving...' : editingDoc ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HipaaCuiDocumentation;

